import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import LogicBaseUserCache from '../../services/cache/LogicBaseUserCache';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import UserAvatar from '../UserAvatar';
import LoadingIndicator from '../LoadingIndicator';
import UserCache from '../../services/cache/UserCache';
const AVATAR_PX = 72;

const ExpertCard = styled.article`
  ${(p) => (p.$layout === 'rail'
    ? `width: 14.5rem;`
    : `width: 100%;
  min-width: 0;`)}
  border-radius: 16px;
  border: 1px solid ${Constants.HairlineBorderRgba};
  background: ${Constants.SurfaceColor};
  padding: ${(p) => (p.$layout === 'rail' ? '0.95rem 0.75rem 0.85rem' : '1.1rem 0.95rem 0.95rem')};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  cursor: ${(p) => (p.$interactive ? 'pointer' : 'default')};
`;

const ExpertAvatarRing = styled.div`
  flex-shrink: 0;
  padding: 4px;
  border-radius: 50%;
  background: ${Constants.isDarkMode
    ? 'linear-gradient(160deg, #2a2a2a 0%, #1c1c1c 100%)'
    : 'linear-gradient(160deg, #fff 0%, #f4f4f5 100%)'};
`;

const ExpertNameRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.65rem;
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
`;

const ExpertNameInner = styled.div`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  min-width: 0;
`;

const ExpertNameTextWrap = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const ExpertNameText = styled.span`
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${Constants.DarkTextColor};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpertBadgeGroup = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.35rem;
`;

const ExpertBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  line-height: 0;
  img {
    height: 14px;
    width: auto;
    display: block;
  }
`;

const ExpertLogicCircleBadge = styled(ExpertBadge)`
  img {
    height: 18px;
  }
`;

const ExpertBio = styled.div`
  width: 100%;
  margin-top: 0.4rem;
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${Constants.MediumTextGray};
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  /* Reserve two lines so tiles align when bio is empty (RailTrack stretches card height). */
  min-height: calc(0.78rem * 1.4 * 2);
`;

/**
 * Expert / people rail tile (Discover “Featured experts”, unified search). Compact card: avatar,
 * name, two-line bio — card opens profile.
 */
export default class LogicBaseExpertCard extends LogictryComponent {
  static propTypes = {
    lbUser: PropTypes.shape({
      _id: PropTypes.string,
    }),
    layout: PropTypes.oneOf(['rail', 'grid']),
  }

  componentDidMount() {
    LogicBaseUserCache.onStateUpdate(this);
  }

  componentWillUnmount() {
    LogicBaseUserCache.offStateUpdate(this);
  }

  render() {
    const { lbUser, layout = 'rail' } = this.props;
    const id = lbUser?._id;
    if (!id) return null;
    LogicBaseUserCache.get(id);
    const users = UserCache.getUsersByIds([id]);
    const user = users && users[0];
    if (!user || user.error) {
      return (
        <ExpertCard $layout={layout} $interactive={false}>
          <LoadingIndicator />
        </ExpertCard>
      );
    }

    const social = LogicBaseUserCache.get(id);
    const verified = social?.verified;
    const logicCircle = social?.logicCircle;
    const bio = (user?.description && String(user.description).trim())
      || (social?.description && String(social.description).trim())
      || '';

    const goProfile = () => Navigation.push(Navigation.userProfile(user));
    const profileLabel = `View profile: ${user.fullname || user.username || 'user'}`;

    return (
      <ExpertCard
        $layout={layout}
        $interactive
        role="link"
        tabIndex={0}
        aria-label={profileLabel}
        onClick={goProfile}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            goProfile();
          }
        }}
      >
        <ExpertAvatarRing>
          <UserAvatar
            user={user}
            widthAndHeight={AVATAR_PX}
          />
        </ExpertAvatarRing>
        <ExpertNameRow>
          <ExpertNameInner>
            <ExpertNameTextWrap>
              <ExpertNameText>{user.fullname || user.username || 'Expert'}</ExpertNameText>
            </ExpertNameTextWrap>
            {(verified || logicCircle) && (
              <ExpertBadgeGroup>
                {verified && (
                  <ExpertBadge>
                    <img src={Constants.VerifiedBadge} alt="Verified" />
                  </ExpertBadge>
                )}
                {logicCircle && (
                  <ExpertLogicCircleBadge>
                    <img src={Constants.LogicCircle} alt="Logic Circle" />
                  </ExpertLogicCircleBadge>
                )}
              </ExpertBadgeGroup>
            )}
          </ExpertNameInner>
        </ExpertNameRow>
        <ExpertBio>{bio}</ExpertBio>
      </ExpertCard>
    );
  }
}
